import { useState } from 'react';
import { useFetch } from '../store/app';
import { Button } from '../components/primitives';
import { SideModal, LabeledText, LabeledArea, LabeledSelect } from '../components/SideModal';
import { api } from '../lib/api';
import type { AuditDetail, AuditInvoice, ClientLocation, Person, ProjectTeamMember } from '../../shared/types';

// Edit the PREA audit specifics (facility, bid, dates, report, month bucket).
export function AuditEditor({ projectId, clientId, audit, onClose, onSaved }: { projectId: number; clientId: number | null; audit: AuditDetail | null; onClose: () => void; onSaved: () => void }) {
  const { data: locations } = useFetch<ClientLocation[]>(clientId ? `/clients/${clientId}/locations` : null);
  const [facility, setFacility] = useState(audit?.facility_location_id ? String(audit.facility_location_id) : '');
  const [bid, setBid] = useState(audit?.bid_amount ? String(audit.bid_amount) : '');
  const [reportType, setReportType] = useState(audit?.report_type || '');
  const [month, setMonth] = useState(audit?.month_bucket || '');
  const [start, setStart] = useState(audit?.audit_start || '');
  const [end, setEnd] = useState(audit?.audit_end || '');
  const [busy, setBusy] = useState(false);

  const locOpts = [{ label: 'None', value: '' }, ...(locations || []).map((l) => ({ label: `${l.label}${l.is_hq ? ' (HQ)' : ''}`, value: String(l.id) }))];

  const save = async () => {
    setBusy(true);
    try {
      await api.put(`/projects/${projectId}/audit`, {
        facility_location_id: facility ? Number(facility) : null,
        bid_amount: Number(bid) || 0,
        report_type: reportType,
        month_bucket: month,
        audit_start: start,
        audit_end: end,
      });
      onSaved();
    } finally {
      setBusy(false);
    }
  };

  return (
    <SideModal
      title="Edit audit details"
      sub="Facility, fieldwork dates, bid & reporting"
      onClose={onClose}
      footer={
        <>
          <span />
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledSelect label="Facility" value={facility} onChange={setFacility} options={locOpts} />
      {!clientId && <div style={{ fontSize: 11.5, color: 'var(--warning)', marginTop: -8, marginBottom: 12 }}>Set a client on the project to pick a facility.</div>}
      <LabeledText label="Bid amount (USD)" value={bid} onChange={setBid} placeholder="e.g. 6500" mono />
      <LabeledSelect label="Report type" value={reportType} onChange={setReportType} options={[{ label: '—', value: '' }, { label: 'Interim', value: 'Interim' }, { label: 'Final', value: 'Final' }, { label: 'n/a', value: 'n/a' }]} />
      <LabeledText label="Audit start" value={start} onChange={setStart} placeholder="e.g. 2025-02-10" />
      <LabeledText label="Audit end" value={end} onChange={setEnd} placeholder="e.g. 2025-02-12" />
      <LabeledText label="Month bucket" value={month} onChange={setMonth} placeholder="e.g. Feb 2025" />
    </SideModal>
  );
}

// Add or edit one auditor on the engagement (with per-auditor fee).
export function AuditorEditor({ projectId, member, onClose, onSaved }: { projectId: number; member: ProjectTeamMember | null; onClose: () => void; onSaved: () => void }) {
  const { data: people } = useFetch<Person[]>('/people');
  const [personId, setPersonId] = useState(member?.person_id ? String(member.person_id) : '');
  const [role, setRole] = useState(member?.role || 'Auditor');
  const [fee, setFee] = useState(member?.fee ? String(member.fee) : '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      if (member) await api.put(`/projects/${projectId}/team/${member.assignment_id}`, { role, fee: Number(fee) || 0 });
      else await api.post(`/projects/${projectId}/team`, { person_id: Number(personId), role, fee: Number(fee) || 0 });
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!member || !confirm(`Remove ${member.name} from this audit?`)) return;
    await api.del(`/projects/${projectId}/team/${member.assignment_id}`);
    onSaved();
  };

  const peopleOpts = [{ label: 'Select staff…', value: '' }, ...(people || []).map((p) => ({ label: `${p.name} · ${p.role}`, value: String(p.id) }))];

  return (
    <SideModal
      title={member ? 'Edit auditor' : 'Add auditor'}
      sub="Assign an auditor and their fee for this audit"
      onClose={onClose}
      footer={
        <>
          {member ? <Button variant="danger" onClick={del}>Remove</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy || (!member && !personId)}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      {member ? (
        <div style={{ marginBottom: 14 }}>
          <div style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-3)', textTransform: 'uppercase', letterSpacing: '.04em', marginBottom: 6 }}>Auditor</div>
          <div style={{ fontSize: 14, fontWeight: 600 }}>{member.name}</div>
        </div>
      ) : (
        <LabeledSelect label="Auditor" value={personId} onChange={setPersonId} options={peopleOpts} />
      )}
      <LabeledSelect label="Role" value={role} onChange={setRole} options={[{ label: 'Lead auditor', value: 'Lead auditor' }, { label: 'Auditor', value: 'Auditor' }, { label: 'Shadow / trainee', value: 'Shadow / trainee' }]} />
      <LabeledText label="Fee (USD)" value={fee} onChange={setFee} placeholder="e.g. 4200" mono />
    </SideModal>
  );
}

// Create / edit an AR invoice for a PREA audit (bill the agency the bid).
export function AuditInvoiceEditor({ projectId, invoice, defaultAmount, onClose, onSaved }: { projectId: number; invoice: AuditInvoice | null; defaultAmount: number; onClose: () => void; onSaved: () => void }) {
  const [amount, setAmount] = useState(String(invoice?.amount ?? defaultAmount ?? ''));
  const [status, setStatus] = useState(invoice?.status || 'ready');
  const [ref, setRef] = useState(invoice?.external_ref || '');
  const [notes, setNotes] = useState(invoice?.notes || '');
  const [busy, setBusy] = useState(false);

  const save = async () => {
    setBusy(true);
    try {
      const payload = { amount, status, external_ref: ref, notes };
      if (invoice) await api.put(`/projects/${projectId}/invoices/${invoice.id}`, payload);
      else await api.post(`/projects/${projectId}/invoices`, payload);
      onSaved();
    } finally {
      setBusy(false);
    }
  };
  const del = async () => {
    if (!invoice || !confirm('Delete this invoice?')) return;
    await api.del(`/projects/${projectId}/invoices/${invoice.id}`);
    onSaved();
  };

  return (
    <SideModal
      title={invoice ? 'Edit invoice' : 'New invoice'}
      sub="Bill the agency — QuickBooks # can be tracked here"
      onClose={onClose}
      footer={
        <>
          {invoice ? <Button variant="danger" onClick={del}>Delete</Button> : <span />}
          <div style={{ display: 'flex', gap: 8 }}>
            <Button variant="secondary" onClick={onClose}>Cancel</Button>
            <Button onClick={save} disabled={busy}>{busy ? 'Saving…' : 'Save'}</Button>
          </div>
        </>
      }
    >
      <LabeledText label="Amount (USD)" value={amount} onChange={setAmount} placeholder="e.g. 6500" mono />
      <LabeledSelect label="Status" value={status} onChange={(v) => setStatus(v as AuditInvoice['status'])} options={[{ label: 'Draft', value: 'draft' }, { label: 'Ready to bill', value: 'ready' }, { label: 'Sent', value: 'sent' }, { label: 'Paid', value: 'paid' }]} />
      <LabeledText label="QuickBooks # (external ref)" value={ref} onChange={setRef} placeholder="e.g. PAOA-1244" mono />
      <LabeledArea label="Notes" value={notes} onChange={setNotes} rows={2} placeholder="Billing instructions, interim/final, etc." />
    </SideModal>
  );
}
